/*
 * Gecombineerde keuze van verzendland en taal.
 *
 * Het domein volgt de TAAL, niet het land: Duits bestaat alleen op lundia.de,
 * elke andere taal op lundia.com. Een Duits bezorgland met een Engelse site
 * blijft dus gewoon op lundia.com.
 *
 * Het localization-formulier van Shopify wisselt alleen binnen het eigen
 * domein, dus een sprong tussen beide domeinen doen we zelf. Shopify lost
 * de vertaalde handle op de bestemming daarna met een 301 op, dus het pad
 * mag gewoon mee.
 */
/*
 * Sinds 2026-09-16 hangt lundia.de aan beide markten, dus elk bezorgland houdt
 * daar zijn eigen prijzen. Daarom staat elke taal open voor elk land: de taal
 * is van de lezer, het land van het pakket. De volgorde stuurt de keuze, niets
 * wordt verborgen.
 */
const LANGUAGE_PREFIX = { nl: "", en: "/en", fr: "/fr" };

/*
 * De taal die bij een bezorgland hoort. Belgie en Luxemburg hebben er twee.
 * Dit bepaalt alleen wat bovenaan staat, niet wat je mag kiezen: de taal is van
 * de lezer en het land van het pakket, en dat hoeft niet hetzelfde te zijn. Een
 * Nederlander die naar Duitsland stuurt houdt dus gewoon zijn Nederlands.
 */
const COUNTRY_LANGUAGES = {
  NL: ["nl"],
  BE: ["nl", "fr"],
  FR: ["fr"],
  LU: ["fr", "de"],
  DE: ["de"],
  AT: ["de"],
};

/* Vaste volgorde voor alles wat niet bij het land of de lezer hoort. */
const LANGUAGE_ORDER = ["nl", "en", "fr", "de"];

/*
 * Welke paginataal "klopt" bij een verzendland, voor het eenmalig openen van de
 * popup. Strenger dan COUNTRY_LANGUAGES: een Belg op de Franse site krijgt de
 * popup wel, Frans staat bij Belgie alleen bovenaan in de keuze. Engels hoort
 * nergens bij, maar op een Engelse pagina opent de popup nooit vanzelf.
 */
const PAGE_LANGUAGES = {
  NL: ["nl"],
  BE: ["nl"],
  FR: ["fr"],
  LU: ["fr", "de"],
  DE: ["de"],
  AT: ["de"],
};

/* Eenmaal gezien (gesloten of opgeslagen) opent de popup nooit meer vanzelf. */
const SEEN_KEY = "lundia-market-language-seen";

/*
 * Testen zonder geolocatie: ?mlm_test=1 negeert de "gezien"-vlag,
 * ?mlm_test=DE doet daarnaast alsof het IP uit Duitsland komt.
 */
const TEST_PARAM = "mlm_test";

class MarketLanguageModal extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector(".market-language-modal__dialog");
    this.form = this.querySelector("form");
    this.countrySelect = this.querySelector("[data-market-language-country]");
    this.languageSelect = this.querySelector("[data-market-language-language]");

    this.basePath = this.dataset.basePath || "/";
    this.comOrigin = this.dataset.comOrigin;
    this.deOrigin = this.dataset.deOrigin;
    /*
     * Het domein uit de browser lezen in plaats van uit Liquid: request.hostname
     * bleek op lundia.de niet 'lundia.de' terug te geven, waardoor de sprong
     * terug naar lundia.com stilletjes uitbleef. De Liquid-waarde blijft als
     * terugval voor het geval er geen window is.
     */
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    this.onDeDomain = host
      ? host === new URL(this.deOrigin).hostname
      : this.dataset.onDeDomain === "true";
    this.currentCountry = this.dataset.currentCountry;
    this.currentLanguage = this.dataset.currentLanguage;

    const i18nTag = this.querySelector("[data-market-language-i18n]");
    this.i18n = i18nTag ? JSON.parse(i18nTag.textContent) : {};

    this.lastFocused = null;
    this.onKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.querySelectorAll("[data-market-language-dismiss]").forEach((el) =>
      el.addEventListener("click", () => this.close())
    );
    /*
     * Land en taal staan helemaal los: een ander land verandert nooit de
     * gekozen taal, alleen de volgorde van de talenlijst.
     */
    this.countrySelect.addEventListener("change", () => this.sortLanguages());
    this.languageSelect.addEventListener("change", () => this.translate());
    this.form.addEventListener("submit", (event) => this.handleSubmit(event));

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-market-language-open]");
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    });

    /*
     * Bij het openen tonen land en taal wat er nu is ingesteld; geen gok. De
     * tekst wisselt pas mee als de bezoeker zelf een taal kiest.
     */
    this.sortLanguages();
    this.translate();
    this.maybeAutoOpen();
  }

  /*
   * Shopify's taal-omleiding staat uit, de land-omleiding aan. Het verzendland
   * klopt dus vrijwel altijd met het IP, maar de taal is die van de pagina
   * waarop je binnenkomt. Past die taal niet bij het verzendland, dan tonen we
   * een keer de popup als controle: met de huidige instelling ingevuld, zodat
   * de bezoeker zelf ziet of het klopt. We gokken geen taal.
   *
   * Nooit vanzelf openen:
   * - als de popup al eens gezien is (gesloten of opgeslagen);
   * - als ?country= in de URL staat: dat is een bewuste keuze, onder meer van
   *   onze eigen sprong tussen lundia.com en lundia.de, die geen gedeelde
   *   opslag hebben;
   * - op een Engelse pagina: wie Engels leest heeft daar meestal zelf voor
   *   gekozen;
   * - als het IP buiten de bezorglanden ligt: Shopify geeft zo iemand een
   *   standaardland, en een popup waarin zijn land ontbreekt jaagt hem weg
   *   voordat hij heeft rondgekeken. Niet als gezien markeren, zodat hij later
   *   vanuit een bezorgland nog een keer de popup kan krijgen;
   * - als het IP-land afwijkt van het verzendland: dan komt het verzendland uit
   *   een eerdere keuze, en die respecteren we.
   */
  async maybeAutoOpen() {
    const params = new URLSearchParams(window.location.search);
    const test = params.get(TEST_PARAM);

    if (!test) {
      if (this.hasSeen()) return;
      if (params.has("country")) {
        this.markSeen();
        return;
      }
    }

    const country = this.currentCountry;
    const language = this.currentLanguage;
    const allowed = PAGE_LANGUAGES[country];
    if (language === "en" || !allowed || allowed.includes(language)) return;

    const detected = test && test.length === 2 ? test.toUpperCase() : await this.detectCountry();
    if (!detected || detected !== country || !PAGE_LANGUAGES[detected]) return;

    /*
     * Leest de bezoeker in zijn eigen browsertaal en past die bij het land (een
     * Waal met een Franse browser op de Franse site), dan klopt het al.
     */
    if (this.browserLanguageFor(detected) === language) return;

    /* Nooit tegelijk met de cookiebanner: eerst die afhandelen. */
    await this.waitForCookieBanner();
    if (!this.hidden) return;

    /* Andere subtitel: "Controleer je taal en verzendland." */
    this.setLead("check");
    this.open(null);
  }

  /*
   * CookieConfirm zet zijn banner (#cc-consent-banner) asynchroon in de pagina,
   * dus eerst even wachten tot die er kan zijn, en daarna tot hij weg is. Klikt
   * de bezoeker de banner nooit weg, dan komt onze popup op deze pagina niet;
   * hij is dan ook niet als gezien gemarkeerd en komt op een volgende pagina.
   */
  waitForCookieBanner() {
    const visible = () => {
      const banner = document.getElementById("cc-consent-banner");
      if (!banner) return false;
      const style = getComputedStyle(banner);
      const rect = banner.getBoundingClientRect();
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        style.opacity !== "0"
      );
    };

    return new Promise((resolve) => {
      const poll = () => (visible() ? setTimeout(poll, 500) : setTimeout(resolve, 400));
      setTimeout(poll, 1500);
    });
  }

  /* Wisselt de subtitel tussen kiezen (handmatig) en controleren (automatisch). */
  setLead(key) {
    const lead = this.querySelector(".market-language-modal__lead");
    if (!lead) return;
    lead.dataset.i18n = key;
    this.translate();
  }

  /* Het land volgens Shopify's eigen geolocatie, of null als dat niet lukt. */
  async detectCountry() {
    try {
      const response = await fetch("/browsing_context_suggestions.json", {
        credentials: "same-origin",
      });
      if (!response.ok) return null;
      const data = await response.json();
      const handle = data && data.detected_values && data.detected_values.country
        ? data.detected_values.country.handle
        : null;
      return handle ? handle.toUpperCase() : null;
    } catch (error) {
      return null;
    }
  }

  /* De eerste browsertaal die bij het land hoort (Belgie: nl of fr). */
  browserLanguageFor(country) {
    const fitting = COUNTRY_LANGUAGES[country] || [];
    const languages = navigator.languages || [navigator.language || ""];
    for (const tag of languages) {
      const code = String(tag).slice(0, 2).toLowerCase();
      if (fitting.includes(code)) return code;
    }
    return null;
  }

  hasSeen() {
    try {
      return window.localStorage.getItem(SEEN_KEY) === "1";
    } catch (error) {
      /* Geen opslag (privévenster): dan liever niet tonen dan elke pagina. */
      return true;
    }
  }

  markSeen() {
    if (new URLSearchParams(window.location.search).has(TEST_PARAM)) return;
    try {
      window.localStorage.setItem(SEEN_KEY, "1");
    } catch (error) {
      /* Niets aan te doen; hasSeen() valt dan ook terug op "gezien". */
    }
  }

  /*
   * Zet de taal van het bezorgland bovenaan, daaronder de taal die je nu leest,
   * en daarna de rest. Er verdwijnt niets: dit stuurt alleen wat je als eerste
   * ziet, zodat een Duitse klant niet zomaar in de Nederlandse site belandt met
   * acties die niet voor hem gelden.
   */
  sortLanguages() {
    const selected = this.languageSelect.value;
    const order = [...(COUNTRY_LANGUAGES[this.countrySelect.value] || [])];

    if (!order.includes(selected)) order.push(selected);
    LANGUAGE_ORDER.forEach((code) => {
      if (!order.includes(code)) order.push(code);
    });

    order.forEach((code) => {
      const option = this.languageSelect.querySelector('option[value="' + code + '"]');
      /* appendChild verplaatst een bestaande optie naar achteren. */
      if (option) this.languageSelect.appendChild(option);
    });

    /* Het verplaatsen van de gekozen optie kan de selectie wissen. */
    this.languageSelect.value = selected;
  }

  /*
   * De modal wisselt van taal zodra je de taal-select verandert, dus nog
   * voordat je opslaat. Daarom staan alle vier de talen in de pagina.
   */
  translate() {
    const lang = this.languageSelect.value;
    const strings = this.i18n[lang];
    if (!strings) return;

    this.querySelectorAll("[data-i18n]").forEach((el) => {
      const value = strings[el.dataset.i18n];
      if (value) el.textContent = value;
    });

    this.querySelectorAll("[data-i18n-aria]").forEach((el) => {
      const value = strings[el.dataset.i18nAria];
      if (value) el.setAttribute("aria-label", value);
    });

    /* De landnamen vertalen niet mee: elk land staat in zijn eigen taal. */
  }

  handleSubmit(event) {
    this.markSeen();
    const country = this.countrySelect.value;
    const language = this.languageSelect.value;
    const wantsDeDomain = language === "de";

    /*
     * Binnen hetzelfde domein doet het Shopify-formulier het werk: het zet
     * land en taal in een keer en stuurt terug naar de vertaalde URL.
     */
    if (wantsDeDomain === this.onDeDomain) return;

    event.preventDefault();

    /*
     * Het gekozen land moet mee over de domeingrens. Een cookie op lundia.com
     * geldt niet op lundia.de, dus zonder deze parameter valt de bezoeker daar
     * terug op het standaardland van dat domein (Oostenrijk) en raakt zijn
     * keuze stil verloren. Shopify leest ?country= en onthoudt hem.
     */
    const prefix = wantsDeDomain ? "" : LANGUAGE_PREFIX[language] || "";
    const target = new URL(
      (wantsDeDomain ? this.deOrigin : this.comOrigin) + prefix + this.basePath
    );
    target.searchParams.set("country", country);
    window.location.href = target.toString();
  }

  open(trigger) {
    this.lastFocused = trigger || document.activeElement;
    this.hidden = false;

    /*
     * De scrollbar verdwijnt zodra we de body op overflow:hidden zetten, en de
     * pagina schuift dan die breedte naar rechts. Compenseren met evenveel
     * padding houdt alles op zijn plek. De header is sticky en staat dus in de
     * normale flow, die schuift vanzelf mee.
     */
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty(
      "--market-language-scrollbar",
      scrollbar + "px"
    );
    document.body.classList.add("market-language-modal-open");
    document.addEventListener("keydown", this.onKeydown);

    /* Reflow forceren in plaats van requestAnimationFrame: rAF vuurt niet in
       een tab die op de achtergrond staat, waardoor de modal wel opent maar
       onzichtbaar blijft. */
    void this.offsetWidth;
    this.classList.add("is-open");
    this.languageSelect.focus();
  }

  close() {
    this.markSeen();
    this.setLead("subtitle");
    this.classList.remove("is-open");
    this.hidden = true;
    document.body.classList.remove("market-language-modal-open");
    document.documentElement.style.removeProperty("--market-language-scrollbar");
    document.removeEventListener("keydown", this.onKeydown);
    if (this.lastFocused) this.lastFocused.focus();
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = this.dialog.querySelectorAll(
      'button, select, a[href], input, [tabindex]:not([tabindex="-1"])'
    );
    const items = Array.from(focusable).filter((el) => !el.disabled && !el.hidden);
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define("market-language-modal", MarketLanguageModal);
